import PropTypes from 'prop-types'
import React from 'react'

/**
 * Skeleton placeholder bar.
 *
 * NOTE: A reusable <Skeleton /> primitive could replace the repeated
 * `bg-charcoal-200 animate-pulse rounded-sm` pattern across:
 *   - components/dealers/DealerLayout/ContactDetailsSection.jsx
 *   - components/dealers/DealerLayout/DealerHeader.jsx
 *   - components/Metrics/MetricCard.jsx (ValueSkeleton)
 *   - This file + RepairOrderChatSkeleton.jsx
 * Tracked for a follow-up task.
 */
const Bar = ({ className = '' }) => (
  <div className={`bg-charcoal-200 animate-pulse rounded-sm ${className}`} />
)

Bar.propTypes = {
  className: PropTypes.string,
}

const InputSkeleton = ({ label }) => (
  <div className="flex w-full flex-col">
    {label ? (
      <div className="flex h-5 w-full flex-row items-center gap-1 pb-1">
        <span className="text-charcoal text-xs font-semibold uppercase leading-[14px] tracking-[1.5px]">
          {label}
        </span>
      </div>
    ) : (
      <Bar className="mb-1 h-3.5 w-20" />
    )}
    <Bar className="h-[42px] w-full rounded-md" />
  </div>
)

InputSkeleton.defaultProps = {
  label: null,
}

InputSkeleton.propTypes = {
  label: PropTypes.string,
}

const LineItemSkeleton = ({ delay }) => {
  const delayClass = delay ? '[animation-delay:150ms]' : ''

  return (
    <div className="mb-8 mt-2">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-3">
          <Bar className={`h-4 w-4 ${delayClass}`} />
          <div className="flex flex-col gap-1.5">
            <Bar className={`h-6 w-36 ${delayClass}`} />
            <Bar className={`h-3.5 w-20 ${delayClass}`} />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Bar className={`h-7 w-20 rounded-lg ${delayClass}`} />
          <Bar className={`h-6 w-24 ${delayClass}`} />
        </div>
      </div>
    </div>
  )
}

LineItemSkeleton.defaultProps = {
  delay: false,
}

LineItemSkeleton.propTypes = {
  delay: PropTypes.bool,
}

/** Skeleton for the RO dates section (right side of header) */
export const RepairOrderDatesSkeleton = () => (
  <div className="flex flex-col gap-3 sm:items-end sm:text-end">
    <div className="flex flex-col sm:items-end">
      <Bar className="h-6 w-52" />
      <Bar className="mt-1 h-3 w-64" />
    </div>
    <div className="flex flex-col gap-1.5 sm:items-end">
      <Bar className="h-4 w-56" />
      <Bar className="h-4 w-44" />
      <Bar className="h-4 w-36" />
    </div>
  </div>
)

/** Skeleton for the general info panel + line items */
export const RepairOrderDetailSkeleton = () => (
  <>
    <div className="bg-brownGray-100 flex w-full flex-col gap-4 rounded-2xl px-6 py-4">
      <div className="flex flex-row items-center justify-between">
        <div className="flex items-center justify-center">
          <span className="text-charcoal-900 text-sm font-bold leading-5">General</span>
        </div>
        <div className="flex items-center gap-2">
          <Bar className="h-7 w-16 rounded-full" />
        </div>
      </div>

      <div className="flex h-full w-full flex-col flex-wrap justify-between gap-4 md:flex-row">
        <InputSkeleton label="REPAIR ORDER #" />
        <InputSkeleton label="Claim Amount" />
        <InputSkeleton label="ADV #" />
        <InputSkeleton />
      </div>
    </div>

    <div className="w-full pt-8">
      <LineItemSkeleton />
      <LineItemSkeleton delay />
    </div>
  </>
)
